import isApp from "shared/util/isApp";
import Analytics from "analytics/util/Analytics";
import Facebook from "shared/util/Facebook";

import { validate, createErrorMessage } from "apps/signup/util/helper";
import {
	FIELDS,
	FACEBOOK_SIGNUP_STATES,
	FACEBOOK_STATE_STRINGS,
} from "apps/signup/util/constants";

import { findAndHandleUnderageError } from "apps/profile_details/util/helpers";

import Api from "apps/signup/util/web";
import { confirmToken } from "ducks/recaptcha";

// ////////////////////////
// ACTION TYPES //////////
// //////////////////////

export const UPDATE_FIELD = "signup.UPDATE_FIELD";
export const VALIDATE_FIELD = "signup.VALIDATE_FIELD";
export const VALIDATE_FIELD_SUCCESS = "signup.VALIDATE_FIELD_SUCCESS";
export const VALIDATE_FIELD_FAIL = "signup.VALIDATE_FIELD_FAIL";
export const DISMISS_SUGGESTION = "signup.DISMISS_SUGGESTION";
export const REDIRECT_TO_ONBOARDING = "signup.REDIRECT_TO_ONBOARDING";

export const SUBMIT_API = "signup.SUBMIT_API";
export const SUBMIT_API_SUCCESS = "signup.SUBMIT_API_SUCCESS";
export const SUBMIT_API_FAIL = "signup.SUBMIT_API_FAIL";

export const MAYBE_SHOW_CAPTCHA = "signup.MAYBE_SHOW_CAPTCHA";
export const HIDE_CAPTCHA = "signup.HIDE_CAPTCHA";
export const SHOW_CAPTCHA = "signup.SHOW_CAPTCHA";
export const STORE_CAPTCHA = "signup.STORE_CAPTCHA";
export const CONFIRM_RECAPTCHA_SUCCESS = "signup.CONFIRM_RECAPTCHA_SUCCESS";
export const SET_RECAPTCHA_V3_TOKEN = "signup.SET_RECAPTCHA_V3_TOKEN";
export const SET_CURRENT_FIELD = "signup.SET_CURRENT_FIELD";

export const FACEBOOK_SIGNUP = "signup.FACEBOOK_SIGNUP";
export const FACEBOOK_SIGNUP_REJECT = "signup.FACEBOOK_SIGNUP_REJECT";
export const FACEBOOK_SIGNUP_MEMBER = "signup.FACEBOOK_SIGNUP_MEMBER";
export const FACEBOOK_SIGNUP_SUCCESS = "signup.FACEBOOK_SIGNUP_SUCCESS";
export const FACEBOOK_SIGNUP_ERROR = "signup.FACEBOOK_SIGNUP_ERROR";


// ////////////////////////
// INITIAL STATE /////////
// //////////////////////

export const INITIAL_STATE = {
	email: "",
	password: "",
	captcha: "",
	captchaV3: "",
	facebookToken: "",

	errors: {},
	feedback: {},
	suggestions: {},
	success: {},

	isValidating: false,
	isUpdating: false,
	seenSuggestion: false,
	isSubmitting: false,
	isSuccess: false,

	showCaptcha: false,
	hasCheckedCaptcha: false,

	facebookState: FACEBOOK_SIGNUP_STATES.ready,
};


// ////////////////////////
// REDUCER ///////////////
// //////////////////////

export function reducer(state = INITIAL_STATE, action) {
	switch (action.type) {
		case UPDATE_FIELD: {
			let {
				errors, feedback, suggestions, facebookState,
			} = state;

			errors = {
				...errors,
				[action.field]: false,
			};

			feedback = {
				...feedback,
				[action.field]: "",
			};

			suggestions = {
				...suggestions,
				[action.field]: false,
			};

			// clear out fb error if they decide to back out
			if (action.field === "email" && errors.facebook) {
				errors.facebook = false;
				facebookState = FACEBOOK_SIGNUP_STATES.ready;
			}

			// clear out registration error if the user updates the password input
			if (action.field === "password") {
				errors.registration = false;
				feedback.registration = "";
			}

			return {
				...state,
				[action.field]: action.data,
				errors,
				feedback,
				suggestions,
				facebookState,
				isUpdating: true,
			};
		}

		case DISMISS_SUGGESTION: {
			return {
				...state,
				seenSuggestion: true,
				isUpdating: false,
			};
		}

		case VALIDATE_FIELD: {
			// reset field properties on every validation
			let {
				errors, feedback, suggestions, success,
			} = state;

			errors = {
				...errors,
				[action.field]: false,
			};

			success = {
				...success,
				[action.field]: false,
			};

			feedback = {
				...feedback,
				[action.field]: "",
			};

			suggestions = {
				...suggestions,
				[action.field]: "",
			};

			return {
				...state,
				errors,
				success,
				feedback,
				suggestions,
				isValidating: true,
				isUpdating: false,
			};
		}

		case VALIDATE_FIELD_SUCCESS: {
			let {
				errors, feedback, suggestions, success,
			} = state;

			errors = {
				...errors,
				[action.field]: false,
			};

			feedback = {
				...feedback,
				[action.field]: action.feedback,
			};

			suggestions = {
				...suggestions,
				[action.field]: action.suggestion,
			};

			success = {
				...success,
				[action.field]: true,
			};

			return {
				...state,
				errors,
				success,
				feedback,
				suggestions,
				isValidating: false,
				seenSuggestion: false,
			};
		}

		case VALIDATE_FIELD_FAIL: {
			let {
				errors, suggestions, feedback, success,
			} = state;

			errors = {
				...errors,
				[action.field]: true,
			};

			feedback = {
				...feedback,
				[action.field]: action.feedback,
			};

			suggestions = {
				...suggestions,
				[action.field]: action.suggestions,
			};

			success = {
				...success,
				[action.field]: false,
			};

			return {
				...state,
				errors,
				success,
				suggestions,
				feedback,
				isValidating: false,
				seenSuggestion: false,
			};
		}

		case SHOW_CAPTCHA: {
			let { errors, feedback } = state;

			errors = {
				...errors,
				captcha: true,
			};

			feedback = {
				...feedback,
				email: "", // clear email and facebook feedback
				facebook: "",
			};

			return {
				...state,
				isSubmitting: false,
				showCaptcha: true,
				errors,
				feedback,
			};
		}

		case MAYBE_SHOW_CAPTCHA: {
			return {
				...state,
				isSubmitting: true,
				hasCheckedCaptcha: true,
			};
		}

		case HIDE_CAPTCHA: {
			return {
				...state,
				isSubmitting: false,
				showCaptcha: false,
			};
		}

		case STORE_CAPTCHA: {
			let { errors } = state;

			errors = {
				...errors,
				captcha: false,
			};

			return {
				...state,
				isSubmitting: false,
				errors,
				captcha: action.value,
			};
		}

		case SUBMIT_API: {
			return {
				...state,
				isSubmitting: true,
			};
		}

		case SUBMIT_API_SUCCESS: {
			return {
				...state,
				isSuccess: true,
				isSubmitting: false,
			};
		}

		case SUBMIT_API_FAIL: {
			let { errors, feedback } = state;

			errors = {
				...errors,
				[action.payload.field]: true,
			};

			feedback = {
				...feedback,
				[action.payload.field]: action.payload.message,
			};

			return {
				...state,
				isSubmitting: false,
				errors,
				feedback,
			};
		}

		case SET_CURRENT_FIELD: {
			return {
				...state,
				currentField: action.field,
			};
		}

		case REDIRECT_TO_ONBOARDING: {
			return state;
		}

		case FACEBOOK_SIGNUP: {
			return {
				...state,
				facebookState: FACEBOOK_SIGNUP_STATES.connecting,
			};
		}

		case FACEBOOK_SIGNUP_MEMBER: {
			return {
				...state,
				facebookState: FACEBOOK_SIGNUP_STATES.loggingIn,
			};
		}

		case FACEBOOK_SIGNUP_SUCCESS: {
			let { errors, feedback } = state;

			errors = {
				...errors,
				"facebook": false,
			};

			feedback = {
				...feedback,
				"facebook": "",
			};

			return {
				...state,
				errors,
				feedback,
				facebookToken: action.accessToken,
				facebookState: FACEBOOK_SIGNUP_STATES.success,
			};
		}

		case FACEBOOK_SIGNUP_ERROR: {
			let { errors, feedback } = state;

			errors = {
				...errors,
				"facebook": true,
			};

			feedback = {
				...feedback,
				"facebook": FACEBOOK_STATE_STRINGS[FACEBOOK_SIGNUP_STATES.errorContinue],
			};

			return {
				...state,
				errors,
				feedback,
				facebookState: FACEBOOK_SIGNUP_STATES.errorContinue,
			};
		}

		case SET_RECAPTCHA_V3_TOKEN: {
			const { payload: { token } } = action;

			return {
				...state,
				captchaV3: token,
			};
		}

		default: {
			return state;
		}
	}
}


// ////////////////////////
// ACTION CREATORS ///////
// //////////////////////

/**
 * Register a new user to mParticle and subsequently fire mParticle login event.
 * @param {string} userid - User ID for fresh user.
 * @param {string} field - Field used to register (Facebook or normal email/password).
 * @returns {Promise} Promise that resolves when UI events for registering have fired.
 */
function _logSuccessfulRegister({ userid, field }) {
	// Synchronous function, but puts all UI events
	// behind a queue to fire only when this has finished.
	Analytics.logInIfNeeded(userid);

	if (field === FIELDS.REGISTRATION) {
		Analytics.logUIEvent(Analytics.eventNames.onboardingRegistered);
		Analytics.logUIEvent(Analytics.eventNames.onboardingSelectedJoinOnRegistration2);
	} else if (field === FIELDS.FACEBOOK) {
		Analytics.logUIEvent(Analytics.eventNames.onboardingRegistered);
	}

	return Promise.resolve(); // promise noop.
}

function _redirectToOnboarding() {
	const destination = "/onboarding";

	if (isApp()) {
		window.App.loadPage(destination, {
			post_login: 1,
			clear_history: 1,
		});
	}
	else {
		window.location.href = destination;
	}
}

function _redirectToHome() {
	if (isApp()) {
		window.App.loadPage("/quickmatch", {
			post_login: 1,
			clear_history: 1,
		});
	} else {
		window.location.href = "/home";
	}
}

export function updateField(field, data) {
	return {
		type: UPDATE_FIELD,
		field,
		data,
	};
}

export function dismissSuggestion() {
	return { type: DISMISS_SUGGESTION };
}

export function storeCaptcha(value) {
	return {
		type: STORE_CAPTCHA,
		value,
	};
}

export function setCaptchaV3Token(token) {
	return {
		type: SET_RECAPTCHA_V3_TOKEN,
		payload: {
			token,
		},
	};
}

export function setCurrentField(field) {
	let currentField;

	if (field) {
		currentField = field;
	} else {
		currentField = FIELDS.EMAIL_OR_FB;
	}

	return {
		type: SET_CURRENT_FIELD,
		field: currentField,
	};
}

export function redirectToOnboarding() {
	_redirectToOnboarding();

	return { type: REDIRECT_TO_ONBOARDING };
}

function confirmCaptchaTokenForPassword(token, isCaptchaShowing) {
	return confirmToken({
		type: CONFIRM_RECAPTCHA_SUCCESS,
		token,
		isCaptchaShowing,
		page: "password",
	});
}


export function maybeShowCaptcha(callbackFunction) {
	return (dispatch, getStore) => {
		dispatch({ type: MAYBE_SHOW_CAPTCHA });

		const store = getStore().signup;

		Api.getShouldShowCaptcha()
			.then((res) => {
				if (res && res.show_captcha === 0) {
					dispatch({ type: HIDE_CAPTCHA });
					dispatch(confirmCaptchaTokenForPassword(store.captchaV3, false));
					return dispatch(callbackFunction());
				} else {
					dispatch(confirmCaptchaTokenForPassword(store.captchaV3, true));
					return dispatch({ type: SHOW_CAPTCHA });
				}
			})
			.catch(() => {
				dispatch(confirmCaptchaTokenForPassword(store.captchaV3, true));
				return dispatch({ type: SHOW_CAPTCHA });
			});
	};
}

export function submitRegistration() {
	return (dispatch, getStore) => {
		const store = getStore().signup;

		// first check if they need captcha;
		if (!store.hasCheckedCaptcha) {
			dispatch(maybeShowCaptcha(submitRegistration));
			return;
		}

		dispatch({ type: SUBMIT_API, field: FIELDS.EMAIL });

		Api.submitRegistration({
			email: store.email,
			password: store.password,
			captcha: store.captcha,
		})
			.then((res) => {
				if (res.user && res.user.userid && res.user.authcode) {
					dispatch({
						type: SUBMIT_API_SUCCESS,
						field: FIELDS.REGISTRATION,
						userid: res.user.userid,
					});
					_logSuccessfulRegister({
						userid: res.user.userid,
						field: FIELDS.REGISTRATION,
					});

					_redirectToOnboarding();
				} else {
					const message = createErrorMessage(res.reasons);

					dispatch({
						type: SUBMIT_API_FAIL,
						payload: {
							field: FIELDS.REGISTRATION,
							message,
						},
						error: true,
					});
				}
			})
			.catch(() => {
				dispatch({
					type: SUBMIT_API_FAIL,
					payload: {
						field: FIELDS.REGISTRATION,
					},
					error: true,
				});
			});
	};
}

export function validateField(field, value) {
	return (dispatch, getStore) => {
		dispatch({
			type: VALIDATE_FIELD,
			field,
		});

		const { seenSuggestion } = getStore().signup;
		const params = {
			field,
			value,
			seenSuggestion,

			// success func
			pass: (success, suggestion) => {
				dispatch({
					type: VALIDATE_FIELD_SUCCESS,
					field,
					suggestion,
					feedback: success,
				});
			},

			// error func
			fail: (error) => {
				dispatch({
					type: VALIDATE_FIELD_FAIL,
					field,
					feedback: error,
				});
			},

			// suggestion
			suggest: (error, suggestions) => {
				dispatch({
					type: VALIDATE_FIELD_FAIL,
					field,
					feedback: error,
					suggestions,
				});
			},
		};


		return validate(params);
	};
}

export function facebookSignup() {
	return (dispatch) => {
		dispatch({ type: FACEBOOK_SIGNUP });

		Facebook.signUp()
			.then((info) => {
				// log them in if they already have an account
				if (info.loginSuccess) {
					dispatch({
						type: FACEBOOK_SIGNUP_MEMBER,
					});
					_redirectToHome();
				} else {
					dispatch({
						type: FACEBOOK_SIGNUP_SUCCESS,
						...info,
					});
				}
			})
			.catch((e) => {
				dispatch({
					type: FACEBOOK_SIGNUP_ERROR,
					error: e,
				});
			});
	};
}

export function facebookSignupReject() {
	return { type: FACEBOOK_SIGNUP_REJECT };
}

export function submitFacebookSignup() {
	return (dispatch, getStore) => {
		const store = getStore().signup;

		dispatch({ type: SUBMIT_API });

		Api.submitFacebookSignup(store.facebookToken, store.captcha)
			.then((res) => {
				if (res.user && res.user.userid && res.user.authcode) {
					dispatch({
						type: SUBMIT_API_SUCCESS,
						field: FIELDS.FACEBOOK,
						userid: res.user.userid,
					});

					_logSuccessfulRegister({
						userid: res.user.userid,
						field: FIELDS.FACEBOOK,
					});

					_redirectToOnboarding();
				} else {
					// boot em if if they can't buy cigarettes
					findAndHandleUnderageError(res.reasons);

					const message = createErrorMessage(res.reasons);

					dispatch({
						type: SUBMIT_API_FAIL,
						payload: {
							field: FIELDS.FACEBOOK,
							message,
						},
						error: true,
					});
				}
			})
			.catch(() => {
				dispatch({
					type: SUBMIT_API_FAIL,
					payload: {
						field: FIELDS.FACEBOOK,
					},
					error: true,
				});
			});
	};
}



// WEBPACK FOOTER //
// ./src/ducks/signup.js